import { useState } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { fetchTitles } from "../api/titleService";
import { DEFAULT_PAGE_SIZE } from "../utils/constants";
import { toSortByParam } from "./useTableSort";
import { dateRangeCriteria } from "../utils/dateRangeFilters";
import { retryUnlessForbidden } from "../utils/httpErrors";
import { usePermissions } from "./usePermissions";
import { useListTotal } from "./useListTotal";
/**
 * Builds the title search criteria from the applied filters.
 *
 * @param filters the applied filters
 * @param publisher the selected publisher; its id replaces the publisher filter value
 * @param publicOnlyView true for the public-only view (e.g. Distributor): the
 *   agency-internal hasCommentForAgency filter is never sent, the backend
 *   refuses it with 403 -92 (DB-837)
 * @returns the search criteria of GET /titles/search
 */
export var buildSearchCriteria = function (filters, publisher, publicOnlyView) {
    var _a;
    if (publicOnlyView === void 0) { publicOnlyView = false; }
    var search = {};
    var id = (_a = filters.id) === null || _a === void 0 ? void 0 : _a.trim();
    if (id) {
        search.id = id;
    }
    if (filters.name) {
        search.name = filters.name;
    }
    if (filters.contributor) {
        search.contributor = filters.contributor;
    }
    if (publisher === null || publisher === void 0 ? void 0 : publisher.id) {
        search.publisher = publisher.id;
    }
    if (filters.productNumber) {
        var normalized = filters.productNumber
            .toUpperCase()
            .replace(/[^0-9X]/g, "");
        if (normalized) {
            search.productNumber = normalized;
        }
    }
    if (filters.collection) {
        search.collection = filters.collection;
    }
    if (!publicOnlyView && filters.hasCommentForAgency) {
        search.hasCommentForAgency = "true";
    }
    Object.assign(search, dateRangeCriteria(filters));
    if (!search.id &&
        !search.name &&
        !search.contributor &&
        !search.publisher &&
        !search.productNumber &&
        !search.collection &&
        !search.hasCommentForAgency &&
        !search.dateCreatedFrom &&
        !search.dateCreatedTo &&
        !search.dateUpdatedFrom &&
        !search.dateUpdatedTo) {
        search.name = "";
    }
    return search;
};
export var useTitleData = function (appliedFilters, pageSize, selectedPublisher, sort, enabled) {
    var _a;
    if (pageSize === void 0) { pageSize = DEFAULT_PAGE_SIZE; }
    if (enabled === void 0) { enabled = true; }
    var ky = useOkapiKy();
    var _b = useState(0), pageOffset = _b[0], setPageOffset = _b[1];
    var searchAsPublicOnly = usePermissions().searchAsPublicOnly;
    var searchCriteria = buildSearchCriteria(appliedFilters, selectedPublisher, searchAsPublicOnly);
    // the backend counts exactly only for the first page — keep that total while paging
    var _c = useListTotal(JSON.stringify(searchCriteria)), totalRecords = _c.totalRecords, recordPage = _c.recordPage;
    var resetPageOffset = function () { return setPageOffset(0); };
    var _d = useQuery(
    // Keyed by what is actually sent: a filter change that leaves the
    // request as it is must not search again.
    [
        "titles",
        searchCriteria,
        pageOffset,
        pageSize,
        sort === null || sort === void 0 ? void 0 : sort.column,
        sort === null || sort === void 0 ? void 0 : sort.direction,
    ], function () {
        return fetchTitles(ky, {
            search: searchCriteria,
            offset: pageOffset,
            limit: pageSize,
            sortBy: sort ? toSortByParam(sort) : undefined,
        });
    }, {
        enabled: enabled,
        keepPreviousData: true,
        // a refused search (403, e.g. -92) does not change on retry
        retry: retryUnlessForbidden,
        onSuccess: function (d) { var _a, _b; return recordPage(pageOffset, d.totalRecords, (_b = (_a = d.items) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); },
    }), data = _d.data, isLoading = _d.isLoading, isError = _d.isError, error = _d.error, refetch = _d.refetch, isFetched = _d.isFetched, isFetching = _d.isFetching;
    var titlesData = (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : [];
    var fetchMoreData = function (offset) {
        setPageOffset(offset);
    };
    return {
        titlesData: titlesData,
        totalRecords: totalRecords,
        isLoading: isLoading,
        /** true while the search waits for its filters (`enabled` false) — no request is running */
        isWaitingForFilters: !enabled,
        isError: isError,
        error: error,
        isFetched: isFetched,
        isFetching: isFetching,
        refetch: refetch,
        fetchMoreData: fetchMoreData,
        resetPageOffset: resetPageOffset,
        pageOffset: pageOffset,
        pageSize: pageSize,
    };
};
